import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, catchError } from 'rxjs';
import { IOrder } from '../models/orders/order';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../../app/models/api';
import { CommonMethods } from '../../app/common-methods';
import { IInvoice } from '../models/invoice/invoice';
import { IOrderNote } from '../models/orders/ordernotes';
import { IInvoiceParts, IInvoiceResponce, ICreatePayment } from '../models/invoice/invoiceResp';

@Injectable({
    providedIn: "root"
})

/**
 * This is the component class OrderService of Service
 * @author Laharee
 * @version 1.0
 * @since 23/05/2023
 */
export class OrderService {
    constructor(
        private http: HttpClient
    ) { }

    //To take Business URL
    orderUrl: string = '';
    //To take company ID
    companyId: string | null = '';
    //To take Client Id
    engineerId: string | null = '';

    /**
     * The URL endpoint for fetching or managing order summaries.
     * This property is expected to be initialized with a valid URL string.
     */
    orderSummaryUrl: string = '';

    /**
    * The URL endpoint for fetching or managing order summaries.
    * This property is expected to be initialized with a valid URL string.
    */
    paymentUrl: string = '';

    /**
     * To set URL
     * Service loads only once, so when business changes, it does not load again
     * BusinessId stays the same and creates authentication problem
     * This function needs to be called each time before API call.
     */
    /**
     * To set URL
     * Service loads only once, so when business changes, it does not load again
     * BusinessId stays the same and creates authentication problem
     * This function needs to be called each time before API call.
     */
    private __init() {
        this.companyId = CommonMethods.getItem("companyId");
        this.orderUrl = `${environment.API_BASE_URL}/job`;
        this.orderSummaryUrl = `${environment.API_BASE_URL}/company/${this.companyId}/parts/invoice`;
        this.paymentUrl = `${environment.API_BASE_URL}/company/${this.companyId}/parts/payment`;
        return this;
    }

    private __setJobUrl() {
        this.orderUrl = `${environment.API_BASE_URL}/job`;
        return this;
    }

    /**
     * To set Image URL
     * @param clientId To assign Client Id
     */
    setOrderUrl(engineerId: number) {
        this.engineerId = CommonMethods.getItem("engineerId");
        this.orderUrl = this.__init().orderUrl + `/engineeers/${this.engineerId}`;
    }


    /**
     * Create a Job
     * @param data
     * @returns  {Observable} returns an observable
     */
    public addOrder(data: IOrder): Observable<ApiResponse<IOrder>> {
        return this.__init().http
            .post<ApiResponse<IOrder>>(`${environment.API_BASE_URL}/company/${this.companyId}/job`, data)
    }

    /**
     * to get the order/s from API
     * all the orders are taken for order page
     * @returns http status
     */
    public getAllOrders(): Observable<ApiResponse<IOrder>> {
        return this.__init().http
            .get<ApiResponse<IOrder>>(this.orderUrl)
    }

    /**
     *  Updates an existing Job
     * @param object data of type IOrderCreate
     * @param number Id of Job
     * @return {Observable} returns an observable
     */
    updateOrder(id: number, data: IOrder): Observable<ApiResponse<IOrder>> {
        return this.__init().http
            .put<ApiResponse<IOrder>>(this.orderUrl + `/${id}`, data)
    }

    /**
     * Delete an Job
     * @param number Id of Job
     * @return {Observable} returns an observable
     */
    public deleteOrder(id?: number): Observable<ApiResponse<IOrder>> {
        return this.__init().http
            .delete<ApiResponse<IOrder>>(this.orderUrl + `/${id}`)
    }

    /**
     * get indivisual order  info by orderId
     * @param id:take order id
     * @returns  {Observable} returns an observable
     */
    public getOrderByEngineer(id: number): Observable<ApiResponse<IOrder>> {
        return this.__init().http
            .get<ApiResponse<IOrder>>(this.orderUrl + `/${id}`)
    }

    public assignJob(data: any): Observable<ApiResponse<IOrder>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IOrder>>(this.orderUrl + `/start`, data)
    }

    public addPartsInJob(data: any): Observable<ApiResponse<IOrder>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IOrder>>(this.orderUrl + `/partsitems`, data)
    }

    /**
     * Remove job partItem by barcode
     * @param data - barcode
     * @returns 
     */
    public removePartsFromJob(data: any): Observable<ApiResponse<IOrder>> {
        return this.__setJobUrl().http
            .delete<ApiResponse<IOrder>>(this.orderUrl + `/partsitems`, { body: data })
    }

    public generateJobInvoice(data: any): Observable<ApiResponse<IInvoice>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IInvoice>>(this.orderUrl + `/invoice`, data)
    }

    public updateJobStatus(data: any): Observable<ApiResponse<IOrder>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IOrder>>(this.orderUrl + `/statusupdate`, data)
    }

    public addPayment(data: any): Observable<ApiResponse<IOrder>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IOrder>>(this.orderUrl + `/payment`, data)
    }

    public updateJobReturnStatus(data: any): Observable<ApiResponse<IOrder>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IOrder>>(this.orderUrl + `/returnstatus`, data)
    }

    public getJobNotes(jobId: any): Observable<ApiResponse<IOrderNote>> {
        return this.__setJobUrl().http
            .get<ApiResponse<IOrderNote>>(this.orderUrl + `/${jobId}/jobnotes`)
    }

    public addJobNotes(data: any): Observable<ApiResponse<IOrderNote>> {
        return this.__setJobUrl().http
            .post<ApiResponse<IOrderNote>>(this.orderUrl + `/jobnotes`, data)
    }

    public getAllOrderList(): Observable<ApiResponse<IInvoiceResponce>> {
        return this.__init().http
            .get<ApiResponse<IInvoiceResponce>>(this.orderSummaryUrl)
    }

    public makePayment(data: any): Observable<ApiResponse<ICreatePayment>> {
        return this.__init().http
            .post<ApiResponse<ICreatePayment>>(this.paymentUrl, data)
    }

    public getAllInvoiceListForOtherCompany(): Observable<ApiResponse<IInvoiceResponce>> {
        return this.__init().http
            .get<ApiResponse<IInvoiceResponce>>(`${environment.API_BASE_URL}/company/${this.companyId}/parts/invoice/favorite`)
    }
}